/** /academy/articles (filters, sort, search) and the category, topic and course pages. */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { GlyphName } from '../../../components/NotificationIcon';
import { cx, Icon } from '../../../components/ui';
import { Link, navigate, useSearch } from '../../../lib/router';
import { courseArticles, searchArticles, sortNewest, sortPopular, topicCounts } from './catalog';
import { ARTICLES } from './content';
import { useReadSet } from './progress';
import { learnPath } from './routes';
import { CATEGORIES, categoryInfo, courseInfo, LEVELS, topicInfo, TOPICS, type CategoryId, type CourseId, type Level, type TopicId } from './taxonomy';
import type { ArticleMeta } from './types';
import { ArticleCard, CardGrid, CATEGORY_ICON, Chip, COURSE_ICON, Crumbs, Empty, IconBadge, LEVEL_BAR, LevelBadge, ProgressBar } from './ui';

const PAGE = 12;

function PageHeader({ crumbs, title, intro, icon, children }: { crumbs: { to?: string; label: string }[]; title: string; intro: string; icon?: [GlyphName, string]; children?: ReactNode }) {
  return (
    <header className="flex flex-col gap-4 pt-8 pb-2">
      <Crumbs items={crumbs} />
      <div className="flex items-center gap-4">
        {icon && <IconBadge icon={icon} size="lg" />}
        <h1 className="m-0 text-[clamp(30px,4.4vw,44px)] leading-tight font-semibold tracking-tight">{title}</h1>
      </div>
      <p className="m-0 max-w-[720px] text-[16.5px] leading-relaxed text-muted">{intro}</p>
      {children}
    </header>
  );
}

function Select<T extends string>({ label, value, options, onChange }: { label: string; value: T | ''; options: { id: T; label: string }[]; onChange: (v: T | '') => void }) {
  return (
    <label className="relative flex min-w-0 items-center">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
        className={cx('h-10 w-full min-w-0 appearance-none rounded-full border bg-card pr-9 pl-4 text-[14px] font-medium text-fg outline-none focus:border-accent', value ? 'border-fg' : 'border-line')}
      >
        <option value="">{label}: All</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="chevron" size={14} className="pointer-events-none absolute right-3.5 text-muted" />
    </label>
  );
}

function Grid({ list, read }: { list: ArticleMeta[]; read: ReadonlySet<string> }) {
  const [shown, setShown] = useState(PAGE);
  const key = list.map((a) => a.slug).join();
  useEffect(() => setShown(PAGE), [key]);
  if (!list.length) return <Empty>No articles match. Try fewer filters or another search.</Empty>;
  return (
    <div className="flex flex-col items-center gap-8">
      <CardGrid className="w-full">
        {list.slice(0, shown).map((a) => (
          <ArticleCard key={a.slug} article={a} read={read.has(a.slug)} />
        ))}
      </CardGrid>
      {shown < list.length && (
        <button type="button" onClick={() => setShown((n) => n + PAGE)} className="h-11 rounded-full border border-line-strong px-6 text-[14px] font-semibold text-fg hover:bg-panel">
          Show more ({list.length - shown})
        </button>
      )}
    </div>
  );
}

// ---------- all articles ----------

type Sort = 'newest' | 'popular';

export function ArticlesPage() {
  const search = useSearch();
  const read = useReadSet();
  const params = new URLSearchParams(search);
  const level = (params.get('level') ?? '') as Level | '';
  const category = (params.get('category') ?? '') as CategoryId | '';
  const topic = (params.get('topic') ?? '') as TopicId | '';
  const sort: Sort = params.get('sort') === 'popular' ? 'popular' : 'newest';
  const urlQ = params.get('q') ?? '';
  const [q, setQ] = useState(urlQ);
  // Back / forward and links change the query; typing doesn't lose a trailing space to it.
  useEffect(() => setQ((cur) => (cur.trim() === urlQ ? cur : urlQ)), [urlQ]);

  const set = (patch: Record<string, string>) => {
    const next = { level, category, topic, sort: sort === 'newest' ? '' : sort, q: urlQ, ...patch };
    navigate(learnPath.articles(next), { replace: true });
  };
  // Typing updates the list at once and the URL a moment later.
  useEffect(() => {
    if (q.trim() === urlQ) return;
    const t = window.setTimeout(() => set({ q: q.trim() }), 300);
    return () => window.clearTimeout(t);
  });

  const list = useMemo(() => {
    let l = ARTICLES.filter((a) => (!level || a.level === level) && (!category || a.category === category) && (!topic || a.topics.includes(topic)));
    l = sort === 'popular' ? sortPopular(l) : sortNewest(l);
    return q.trim() ? searchArticles(l, q) : l;
  }, [level, category, topic, sort, q]);
  const counts = topicCounts(ARTICLES);
  const filtered = !!(level || category || topic || q.trim());

  return (
    <div className="flex flex-col gap-8">
      <PageHeader crumbs={[{ to: learnPath.home(), label: 'Academy' }, { label: 'Articles' }]} title="All articles" intro="Every Academy article in one place. Filter by level, category and topic, or search for what you want to learn." />
      <div className="flex flex-col gap-4 rounded-[20px] border border-line bg-panel p-4 md:p-5">
        <div className="relative">
          <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-muted" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, sections and topics"
            aria-label="Search articles"
            className="h-12 w-full rounded-full border border-line bg-card pr-4 pl-11 text-[15px] text-fg outline-none placeholder:text-muted focus:border-accent"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Level" className="flex flex-wrap gap-1.5">
            {[{ id: '' as const, label: 'All levels' }, ...LEVELS].map((l) => (
              <button
                key={l.id || 'all'}
                type="button"
                aria-pressed={level === l.id}
                onClick={() => set({ level: l.id })}
                className={cx('h-10 rounded-full px-4 text-[14px] font-medium', level === l.id ? 'bg-fg text-bg' : 'bg-card text-fg hover:bg-pill')}
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="grid w-full grid-cols-2 gap-2 sm:w-auto sm:grid-cols-[180px_220px]">
            <Select label="Category" value={category} options={CATEGORIES.map((c) => ({ id: c.id, label: c.title }))} onChange={(v) => set({ category: v })} />
            <Select label="Topic" value={topic} options={TOPICS.map((t) => ({ id: t.id, label: `${t.title} (${counts.get(t.id) ?? 0})` }))} onChange={(v) => set({ topic: v })} />
          </div>
          <div className="flex-1" />
          <div role="group" aria-label="Sort" className="flex rounded-full bg-card p-1">
            {(['newest', 'popular'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={sort === s} onClick={() => set({ sort: s === 'newest' ? '' : s })} className={cx('h-8 rounded-full px-3.5 text-[13.5px] font-semibold capitalize', sort === s ? 'bg-fg text-bg' : 'text-muted hover:text-fg')}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[14px] text-muted">
        <span role="status">
          {list.length} {list.length === 1 ? 'article' : 'articles'}
          {q.trim() ? ` for “${q.trim()}”` : ''}
        </span>
        {filtered && (
          <button type="button" onClick={() => (setQ(''), navigate(learnPath.articles(sort === 'popular' ? { sort } : {}), { replace: true }))} className="font-semibold text-fg hover:text-accent-fg">
            Clear filters
          </button>
        )}
      </div>
      <Grid list={list} read={read} />
    </div>
  );
}

// ---------- category / topic ----------

function LevelFilter({ list, read }: { list: ArticleMeta[]; read: ReadonlySet<string> }) {
  const [level, setLevel] = useState<Level | ''>('');
  const shown = level ? list.filter((a) => a.level === level) : list;
  return (
    <>
      <div role="group" aria-label="Level" className="flex flex-wrap gap-1.5">
        {[{ id: '' as const, label: 'All levels' }, ...LEVELS].map((l) => {
          const n = l.id ? list.filter((a) => a.level === l.id).length : list.length;
          return (
            <button
              key={l.id || 'all'}
              type="button"
              aria-pressed={level === l.id}
              disabled={!n}
              onClick={() => setLevel(l.id)}
              className={cx('h-10 rounded-full px-4 text-[14px] font-medium disabled:opacity-40', level === l.id ? 'bg-fg text-bg' : 'bg-panel text-fg hover:bg-pill')}
            >
              {l.label} <span className="num opacity-60">{n}</span>
            </button>
          );
        })}
      </div>
      <Grid list={shown} read={read} />
    </>
  );
}

export function NotFound({ what }: { what: string }) {
  return (
    <div className="flex flex-col items-center gap-4 py-24 text-center">
      <h1 className="m-0 text-[28px] font-semibold">{what} not found</h1>
      <p className="m-0 text-muted">It may have moved. Try the full list of articles.</p>
      <Link to={learnPath.articles()} className="inline-flex h-11 items-center rounded-full bg-accent px-5 font-semibold text-white no-underline">
        All articles
      </Link>
    </div>
  );
}

export function CategoryPage({ id }: { id: CategoryId }) {
  const read = useReadSet();
  const c = categoryInfo(id);
  if (!c) return <NotFound what="Category" />;
  const list = sortNewest(ARTICLES.filter((a) => a.category === c.id));
  return (
    <div className="flex flex-col gap-8">
      <PageHeader crumbs={[{ to: learnPath.home(), label: 'Academy' }, { to: learnPath.articles(), label: 'Articles' }, { label: c.title }]} title={c.title} intro={c.blurb} icon={CATEGORY_ICON[c.id]}>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((x) => (
            <Chip key={x.id} to={learnPath.category(x.id)} active={x.id === c.id}>
              {x.title}
            </Chip>
          ))}
        </div>
      </PageHeader>
      <LevelFilter list={list} read={read} />
    </div>
  );
}

export function TopicPage({ id }: { id: TopicId }) {
  const read = useReadSet();
  const t = topicInfo(id);
  if (!t) return <NotFound what="Topic" />;
  const list = sortNewest(ARTICLES.filter((a) => a.topics.includes(t.id)));
  const counts = topicCounts(ARTICLES);
  return (
    <div className="flex flex-col gap-8">
      <PageHeader crumbs={[{ to: learnPath.home(), label: 'Academy' }, { to: learnPath.articles(), label: 'Topics' }, { label: t.title }]} title={t.title} intro={`${t.blurb} ${list.length} ${list.length === 1 ? 'article' : 'articles'}.`} />
      <LevelFilter list={list} read={read} />
      <section className="flex flex-col gap-3 border-t border-line pt-8">
        <h2 className="m-0 text-[18px] font-semibold">Other topics</h2>
        <div className="flex flex-wrap gap-2">
          {TOPICS.filter((x) => x.id !== t.id).map((x) => (
            <Chip key={x.id} to={learnPath.topic(x.id)}>
              {x.title} <span className="num text-[12px] text-muted">{counts.get(x.id) ?? 0}</span>
            </Chip>
          ))}
        </div>
      </section>
    </div>
  );
}

// ---------- course ----------

export function CoursePage({ id }: { id: CourseId }) {
  const read = useReadSet();
  const c = courseInfo(id);
  if (!c) return <NotFound what="Course" />;
  const lessons = courseArticles(ARTICLES, c.id);
  const done = lessons.filter((l) => read.has(l.slug)).length;
  const next = lessons.find((l) => !read.has(l.slug)) ?? lessons[0];
  const minutes = lessons.reduce((t, l) => t + l.readTime, 0);
  const total = Math.max(lessons.length, ...lessons.map((l) => l.order));
  const tone = LEVEL_BAR[c.level ?? 'beginner'];
  return (
    <div className="flex flex-col gap-8">
      <PageHeader crumbs={[{ to: learnPath.home(), label: 'Academy' }, { label: 'Courses' }, { label: c.title }]} title={c.title} intro={c.intro} icon={COURSE_ICON[c.id]}>
        <div className="flex flex-wrap items-center gap-3 text-[14px] text-muted">
          {c.level ? <LevelBadge level={c.level} /> : <span className="inline-flex h-6 items-center rounded-md bg-pill px-2 text-[12px] font-semibold text-fg">All levels</span>}
          <span>
            {lessons.length} lessons · about {minutes} minutes of reading
          </span>
        </div>
      </PageHeader>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <ol className="m-0 flex list-none flex-col gap-0 overflow-hidden rounded-[20px] border border-line p-0">
          {lessons.map((l) => {
            const isRead = read.has(l.slug);
            return (
              <li key={l.slug} className="border-b border-line last:border-b-0">
                <Link to={learnPath.article(l.slug)} className="group flex items-start gap-4 px-4 py-4 text-fg no-underline hover:bg-panel md:px-6">
                  <span className={cx('num mt-0.5 grid size-9 shrink-0 place-items-center rounded-full text-[13.5px] font-semibold', isRead ? 'bg-up text-white' : 'bg-track text-muted')}>{isRead ? <Icon name="check" size={15} /> : l.order}</span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[12px] font-semibold tracking-[0.06em] text-muted uppercase">
                      Lesson {l.order} of {total}
                    </span>
                    <span className="text-[16.5px] leading-snug font-semibold group-hover:text-accent-fg">{l.title}</span>
                    <span className="line-clamp-2 text-[14px] leading-relaxed text-muted">{l.description}</span>
                  </span>
                  <span className="mt-1 hidden shrink-0 flex-col items-end gap-1.5 text-[12.5px] text-muted sm:flex">
                    <LevelBadge level={l.level} />
                    {l.readTime} min
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
        <aside className="flex flex-col gap-4 self-start rounded-[20px] border border-line bg-panel p-5 lg:sticky lg:top-24">
          <span className="text-[15px] font-semibold">Your progress</span>
          <ProgressBar done={done} total={lessons.length} tone={tone} label={`${done} of ${lessons.length} lessons read`} />
          <span className="text-[13.5px] text-muted">
            {done} of {lessons.length} lessons read in this browser.
          </span>
          {next && (
            <Link to={learnPath.article(next.slug)} className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-accent px-5 text-[14px] font-semibold text-white no-underline">
              {done === 0 ? 'Start lesson 1' : done === lessons.length ? 'Review from lesson 1' : `Continue: lesson ${next.order}`} <Icon name="right" size={15} />
            </Link>
          )}
        </aside>
      </div>
    </div>
  );
}
